import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import "./ForgotPassword.css";
import logo from "assets/img/samu01_logo.png";
import logoomnidoc from "assets/img/logoomnidoc.png";
import { forgotPassword, verifyResetOtp, resetPassword, checkToken } from "../../../api/api";

function ForgotPassword() {
    const toast = useToast();
    const navigate = useNavigate();

    const [step, setStep] = useState("request");
    const [loading, setLoading] = useState(false);

    const [telephone, setTelephone] = useState("");
    const [otp, setOtp] = useState("");
    const [nouveauMotDePasse, setNouveauMotDePasse] = useState("");
    const [confirmationMotDePasse, setConfirmationMotDePasse] = useState("");

    const [userId, setUserId] = useState(null);
    const [resetToken, setResetToken] = useState("");

    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    const normalizePhoneInput = (value) => value.replace(/[\s\-()]/g, "").trim();

    const isValidPhone = (value) => {
        const cleaned = normalizePhoneInput(value);
        return /^0[567]\d{8}$/.test(cleaned) || /^\+?212[567]\d{8}$/.test(cleaned);
    };

    const isValidOtp = (value) => /^\d{6}$/.test(String(value || "").trim());

    const backToRequestStep = () => {
        setStep("request");
        setOtp("");
        setResetToken("");
        setNouveauMotDePasse("");
        setConfirmationMotDePasse("");
        setUserId(null);
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();

        if (!isValidPhone(telephone)) {
            toast({
                title: "Téléphone invalide",
                description: "Saisissez un numéro valide (ex: 06XXXXXXXX).",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);
        try {
            const response = await forgotPassword(normalizePhoneInput(telephone));
            if (response.data.userId) {
                setUserId(response.data.userId);
                setStep("verify");
                setOtp("");
                toast({
                    title: "Code envoyé",
                    description: response.data.message || "Un code de récupération a été envoyé par SMS.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Vérifiez vos informations",
                    description: response.data.message || "Impossible d'initier la récupération.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Erreur",
                description: error.response?.data?.message || "Une erreur est survenue.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
        setLoading(false);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();

        if (!userId) {
            toast({
                title: "Session expirée",
                description: "Veuillez redemander un code OTP.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            backToRequestStep();
            return;
        }

        if (!isValidOtp(otp)) {
            toast({
                title: "Code OTP invalide",
                description: "Le code OTP doit contenir exactement 6 chiffres.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);
        try {
            const response = await verifyResetOtp(userId, otp.trim());
            if (response.data.resetToken) {
                setResetToken(response.data.resetToken);
                setStep("reset");
                setNouveauMotDePasse("");
                setConfirmationMotDePasse("");
                toast({
                    title: "Code vérifié",
                    description: response.data.message || "Code OTP validé.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Erreur",
                    description: "Session de réinitialisation non créée.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Erreur OTP",
                description: error.response?.data?.message || "Code OTP invalide ou expiré.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
        setLoading(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();

        if (!userId || !resetToken) {
            toast({
                title: "Session expirée",
                description: "Veuillez recommencer la récupération du mot de passe.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            backToRequestStep();
            return;
        }

        if (nouveauMotDePasse.length < 8) {
            toast({
                title: "Mot de passe trop court",
                description: "Le mot de passe doit contenir au moins 8 caractères.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        if (nouveauMotDePasse !== confirmationMotDePasse) {
            toast({
                title: "Confirmation invalide",
                description: "Les deux mots de passe ne correspondent pas.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);
        try {
            const response = await resetPassword(userId, null, nouveauMotDePasse, resetToken);

            let isSessionReady = false;
            try {
                const tokenCheck = await checkToken();
                isSessionReady = !!tokenCheck?.data?.valid;
            } catch (sessionErr) {
                isSessionReady = false;
            }

            toast({
                title: "Succès",
                description: response.data.message || "Mot de passe réinitialisé avec succès. Connexion confirmée.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            if (isSessionReady) {
                navigate("/admin/default");
            } else {
                navigate("/auth/sign-in");
            }
        } catch (error) {
            toast({
                title: "Erreur",
                description: error.response?.data?.message || "Code OTP incorrect ou erreur lors de la réinitialisation.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
        setLoading(false);
    };

    return (
        <div className="auth-wrapper">
            {/* Header */}
            <header className="auth-header">
                <div className="auth-header-title">Récupération de compte</div>
                <div className="auth-header-actions">
                    <div className="auth-language-selector">
                        🇫🇷 Français ▼
                    </div>
                </div>
            </header>

            {/* Main Container */}
            <main className="auth-main-container">
                <div className="auth-card">
                    {/* Logo Section */}
                    <div className="auth-logo-container">
                        <img src={logo} alt="OMNIDOC Logo" className="auth-logo" />
                    </div>

                    {/* Form Title & Subtitle */}
                    <h1 className="auth-form-title">
                        {step === "request"
                            ? "Mot de passe oublié ?"
                            : step === "verify"
                                ? "Vérifier le code OTP"
                                : "Réinitialiser le mot de passe"}
                    </h1>
                    <p className="auth-form-subtitle">
                        {step === "request"
                            ? "Entrez votre numéro de téléphone pour recevoir un code de récupération."
                            : step === "verify"
                                ? "Saisissez le code OTP reçu par SMS pour continuer."
                                : "Saisissez et confirmez votre nouveau mot de passe."}
                    </p>

                    {/* Form */}
                    {step === "request" ? (
                        <form onSubmit={handleRequestOtp}>
                            <div className="auth-form-group">
                                <label htmlFor="telephone" className="auth-form-label">Téléphone</label>
                                <input
                                    type="text"
                                    id="telephone"
                                    className="auth-form-input"
                                    placeholder="06XXXXXXXX"
                                    required
                                    value={telephone}
                                    onChange={(e) => setTelephone(e.target.value)}
                                />
                            </div>

                            <button type="submit" className="auth-submit-btn" disabled={loading}>
                                {loading ? "Envoi..." : "Envoyer le code"}
                            </button>

                            <div className="auth-forgot-password">
                                <NavLink to="/auth/sign-in">Retour à la connexion</NavLink>
                            </div>
                        </form>
                    ) : step === "verify" ? (
                        <form onSubmit={handleVerifyOtp}>
                            <div className="auth-form-group">
                                <label htmlFor="otp" className="auth-form-label">Code OTP</label>
                                <input
                                    type="text"
                                    id="otp"
                                    className="auth-form-input"
                                    placeholder="Code reçu par SMS"
                                    required
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    autoFocus
                                />
                            </div>

                            <button type="submit" className="auth-submit-btn" disabled={loading}>
                                {loading ? "Vérification..." : "Vérifier le code"}
                            </button>

                            <div className="auth-forgot-password">
                                <button
                                    type="button"
                                    onClick={backToRequestStep}
                                    style={{ background: "none", border: "none", color: "#0066cc", cursor: "pointer", fontSize: "0.9rem" }}
                                >
                                    Retour
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={handleResetPassword}>
                            <div className="auth-form-group">
                                <label htmlFor="password" title="Nouveau mot de passe" className="auth-form-label">Nouveau mot de passe</label>
                                <div className="auth-password-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        className="auth-form-input"
                                        placeholder="••••••••••••••••••••••"
                                        required
                                        value={nouveauMotDePasse}
                                        onChange={(e) => setNouveauMotDePasse(e.target.value)}
                                    />
                                    <button type="button" className="auth-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? "🙊" : "👁"}
                                    </button>
                                </div>
                            </div>

                            <div className="auth-form-group">
                                <label htmlFor="confirmPassword" className="auth-form-label">Confirmer le mot de passe</label>
                                <div className="auth-password-wrapper">
                                    <input
                                        type={showPasswordConfirm ? "text" : "password"}
                                        id="confirmPassword"
                                        className="auth-form-input"
                                        placeholder="••••••••••••••••••••••"
                                        required
                                        value={confirmationMotDePasse}
                                        onChange={(e) => setConfirmationMotDePasse(e.target.value)}
                                    />
                                    <button type="button" className="auth-password-toggle" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}>
                                        {showPasswordConfirm ? "🙊" : "👁"}
                                    </button>
                                </div>
                            </div>

                            <button type="submit" className="auth-submit-btn" disabled={loading}>
                                {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
                            </button>

                            <div className="auth-forgot-password">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep("verify");
                                        setNouveauMotDePasse("");
                                        setConfirmationMotDePasse("");
                                    }}
                                    style={{ background: "none", border: "none", color: "#0066cc", cursor: "pointer", fontSize: "0.9rem" }}
                                >
                                    Retour
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </main>

            <div className="auth-card-footer">
                
            </div>

            <div className="auth-page-footer">
                <p>En continuant, vous acceptez nos <NavLink to="#">Conditions d'utilisation</NavLink> et notre <NavLink to="#">Politique de confidentialité</NavLink>. <a href="mailto:support@omnidoc.com">Contactez le support</a></p>
            </div>
        </div>
    );
}

export default ForgotPassword;
